import { authFetch } from 'auth/utils';
import { ObjectValues } from 'common/tsHelpers';
import saveAs from 'file-saver';
import { envVariables } from 'src/common/utils/envVariables';
import { z } from 'zod';

export const POLLING_RESPONSE = {
  ACK: 0,
  NACK: 1,
  NO_RESPONSE: 2,
} as const;

export type PollingResponse = ObjectValues<typeof POLLING_RESPONSE>;

export const PROFILE = {
  PROFILE_0: 0,
  PROFILE_1: 1,
} as const;

const pollingMessageDtoSchema = z.object({
  serverId: z.number(),
  profileId: z.union([z.literal(PROFILE.PROFILE_0), z.literal(PROFILE.PROFILE_1)]),
  pollingResponse: z.union([
    z.literal(POLLING_RESPONSE.ACK),
    z.literal(POLLING_RESPONSE.NACK),
    z.literal(POLLING_RESPONSE.NO_RESPONSE),
  ]),
  timestamp: z.string(),
  isPaused: z.boolean(),
});

const pollingMessagesDtoSchema = z.object({
  items: z.array(pollingMessageDtoSchema),
  pageNumber: z.number(),
  totalPages: z.number(),
  totalCount: z.number(),
  hasPreviousPage: z.boolean(),
  hasNextPage: z.boolean(),
  error: z.string().optional(),
});

export const fetchPollingHistory = async (
  euicc: string,
  from: Date,
  to: Date,
  page: number = 0,
  pageSize: number = 50,
  signal?: AbortSignal,
) => {
  const urlQuery = new URLSearchParams({
    PageNumber: (page + 1).toString(),
    PageSize: pageSize.toString(),
    DateFrom: from.toISOString(),
    DateTo: to.toISOString(),
  });
  const pollingResponse = await authFetch(
    `${
      envVariables.VITE_SIM_INVENTORY_RSIMS_PROFILE_API
    }/${euicc}/polling-history?${urlQuery.toString()}`,
    { signal },
  );

  if (!pollingResponse.ok) {
    throw new Error(
      `Failed to fetch polling history: ${pollingResponse.status} ${pollingResponse.statusText}`,
    );
  }

  const pollingData = (await pollingResponse.json()) as unknown;
  const parsedPollingData = pollingMessagesDtoSchema.safeParse(pollingData);

  if (!parsedPollingData.success) {
    throw new Error('Polling history response has an unexpected format.');
  }

  return parsedPollingData.data;
};

const downloadFile = async (filePath: string) => {
  const getFileNameFromContentDispositionHeader = (headerValue: string | null) =>
    headerValue
      ?.split(';')
      ?.find((n) => n.includes('filename='))
      ?.replace('filename=', '')
      .trim()
      .replaceAll('"', '');

  const fileResponse = await authFetch(filePath);

  const filename = getFileNameFromContentDispositionHeader(
    fileResponse.headers.get('Content-Disposition'),
  );

  const blob = await fileResponse.blob();

  saveAs(blob, filename);

  return fileResponse;
};

export const downloadPollingHistory = async (euicc: string, from: Date, to: Date) => {
  const urlQuery = new URLSearchParams({
    DateFrom: from.toISOString(),
    DateTo: to.toISOString(),
    TimeZoneIdentifier: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const response = await downloadFile(
    `${
      envVariables.VITE_SIM_INVENTORY_REPORTING_API
    }/rsim-profiles/${euicc}/polling-history?${urlQuery.toString()}`,
  );

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  return response;
};
